import {
  HomeOpenActionDto,
  HomeTodoCategory,
  HomeTodoDto,
} from "@coaching-culture/types";
import { formatDate, formatRelative } from "@coaching-culture/ui";
import {
  addDays,
  endOfDay,
  formatDistanceStrict,
  formatDistanceToNowStrict,
  fromUnixTime,
  getUnixTime,
  isToday,
  startOfDay,
  subHours,
} from "date-fns";
import { clientUrlForContent } from "../../utils/content";

export type TodoGroupId = "overdue" | "today" | "week" | "later" | "anytime";

export const groupLabels: Record<TodoGroupId, string> = {
  overdue: "Overdue",
  today: "Today",
  week: "Next 7 days",
  later: "Later",
  anytime: "Anytime",
};

export const groupOrder: TodoGroupId[] = [
  "overdue",
  "today",
  "week",
  "later",
  "anytime",
];

export const categoryLabels: Record<HomeTodoCategory, string> = {
  conversations: "Conversations",
  feedback: "Feedback",
  goals: "Goals",
  learning: "Learning",
};

/**
 * A ticked-off action stays on the list this long, in the group it was already
 * in. Must match COMPLETED_WINDOW_HOURS on the API, which stops sending them.
 */
export const COMPLETED_WINDOW_HOURS = 24;

/** When the item was ticked off, for the kinds that can be. */
export function completedAt(item: HomeTodoDto): number | null {
  return item.kind === "conversation-action" ? item.completedOn : null;
}

export const isCompleted = (item: HomeTodoDto) => completedAt(item) != null;

/** Items done longer ago than the window: a tab left open overnight. */
export function isStaleCompletion(
  item: HomeTodoDto,
  now = new Date(),
): boolean {
  const done = completedAt(item);
  return (
    done != null && fromUnixTime(done) < subHours(now, COMPLETED_WINDOW_HOURS)
  );
}

/** Replaces the due text once an item is done. */
export function completedText(
  item: HomeTodoDto,
  now = new Date(),
): string | null {
  const done = completedAt(item);
  if (done == null) {
    return null;
  }
  return getUnixTime(now) - done < 60
    ? "Done just now"
    : `Done ${formatDistanceStrict(fromUnixTime(done), now, {
        addSuffix: true,
      })}`;
}

/** Overdue means the due day has ended, in the viewer's own time zone. */
export function isOverdue(item: HomeTodoDto, now = new Date()): boolean {
  return (
    item.dueDate != null &&
    endOfDay(fromUnixTime(item.dueDate)) < startOfDay(now)
  );
}

export function groupFor(item: HomeTodoDto, now = new Date()): TodoGroupId {
  if (item.dueDate == null) {
    return "anytime";
  }
  const due = fromUnixTime(item.dueDate);
  if (isOverdue(item, now)) {
    return "overdue";
  }
  if (due <= endOfDay(now)) {
    return "today";
  }
  if (due <= endOfDay(addDays(now, 7))) {
    return "week";
  }
  return "later";
}

export function groupTodos(items: HomeTodoDto[], now = new Date()) {
  const groups: Record<TodoGroupId, HomeTodoDto[]> = {
    overdue: [],
    today: [],
    week: [],
    later: [],
    anytime: [],
  };
  for (const item of items) {
    groups[groupFor(item, now)].push(item);
  }
  return groups;
}

/** The tag shown on each row. */
export function tagLabel(item: HomeTodoDto): string {
  switch (item.kind) {
    case "conversation-action":
      return "Action";
    case "conversation-prep":
      return "Conversation";
    case "survey":
      return "Survey";
    case "give-360":
    case "request-360":
      return "360";
    case "feedback-request":
      return "Request";
    case "measures":
      return "Measures";
    case "goal-update":
      return "Goal";
    case "assessment":
      return "Assessment";
    case "mindset-challenge":
      return "Challenge";
    case "content":
      return item.content.type === "module"
        ? "Mindset"
        : item.content.type === "lesson"
          ? "Lesson"
          : item.content.type === "learncast"
            ? "LearnCast"
            : "Assessment";
  }
}

export type TodoAction =
  | { type: "link"; label: string; to: string }
  /** A toggle: pressed once it is done, which undoes it. */
  | { type: "complete"; label: string; done: boolean };

/** The one button on each row, and where the row itself links to. */
export function primaryAction(item: HomeTodoDto): TodoAction {
  switch (item.kind) {
    case "conversation-action":
      return item.completedOn != null
        ? { type: "complete", label: "Done", done: true }
        : { type: "complete", label: "Mark done", done: false };
    case "conversation-prep":
      return {
        type: "link",
        label: "Prepare",
        to: conversationUrl(item.conversationId, item.occurrenceIndex),
      };
    case "survey":
      return {
        type: "link",
        label: "Start",
        to: `/solutions/surveys/${item.surveyId}`,
      };
    case "give-360":
      return {
        type: "link",
        label: "Give feedback",
        to: `/feedback?survey=${encodeURIComponent(item.code)}`,
      };
    case "request-360":
      return {
        type: "link",
        label: "Choose people",
        to: `/solutions/feedback/campaigns/${item.campaignUserId}`,
      };
    case "feedback-request":
      return {
        type: "link",
        label: "Reply",
        to: `/solutions/continuous-feedback/send/${item.requesterId}/${item.requestId}`,
      };
    case "measures":
      return { type: "link", label: "Update", to: "/solutions/my-team" };
    case "goal-update":
      return {
        type: "link",
        label: "Update",
        to: `/solutions/performance/goals/${item.goalId}/review`,
      };
    case "assessment":
    case "content":
      return {
        type: "link",
        label: "Start",
        to: clientUrlForContent(item.content),
      };
    case "mindset-challenge":
      return {
        type: "link",
        label: "Report back",
        to: `${clientUrlForContent(item.content)}/challenge-response`,
      };
  }
}

export const conversationUrl = (id: string, index: number) =>
  `/solutions/performance/conversations/${id}/${index}`;

export const conversationPlayUrl = (id: string, index: number) =>
  `${conversationUrl(id, index)}/play`;

/** The due-date part of a row's context line. */
export function dueText(item: HomeTodoDto, now = new Date()): string | null {
  if (item.kind === "goal-update") {
    const updated = `Last updated ${formatDistanceToNowStrict(
      fromUnixTime(item.lastUpdated),
      { addSuffix: true },
    )}`;
    if (item.dueDate == null) {
      return updated;
    }
    return isOverdue(item, now)
      ? `Deadline was ${formatDate(item.dueDate, now)}`
      : `Deadline ${formatDate(item.dueDate, now)}`;
  }

  if (item.dueDate == null) {
    return null;
  }

  if (item.kind === "conversation-prep") {
    return formatRelative(item.dueDate);
  }

  if (item.kind === "measures") {
    return null;
  }

  return isOverdue(item, now)
    ? `Was due ${formatDate(item.dueDate, now)}`
    : isToday(fromUnixTime(item.dueDate))
      ? "Due today"
      : `Due ${formatDate(item.dueDate, now)}`;
}

export type ActionOwnerGroup = {
  key: string;
  /** "Your actions", "Marc's actions" or "Unassigned". */
  label: string;
  actions: HomeOpenActionDto[];
  overdue: number;
};

/**
 * Splits a conversation's open actions by who they're assigned to, so the
 * brief can list "Your actions" and "Marc's actions" separately. The viewer's own
 * actions come first, then other people, then unassigned ones.
 */
export function groupActionsByOwner(
  actions: HomeOpenActionDto[],
  viewerId: string,
  now = new Date(),
): ActionOwnerGroup[] {
  const groups = new Map<string, ActionOwnerGroup>();

  for (const action of actions) {
    const owner = action.assignedTo;
    const key = owner == null ? "none" : owner.id;
    const label =
      owner == null
        ? "Unassigned"
        : owner.id === viewerId
          ? "Your actions"
          : `${owner.name.split(" ")[0] || owner.name}'s actions`;

    const group = groups.get(key) ?? { key, label, actions: [], overdue: 0 };
    group.actions.push(action);
    if (action.dueDate != null && fromUnixTime(action.dueDate) < now) {
      group.overdue += 1;
    }
    groups.set(key, group);
  }

  const rank = (g: ActionOwnerGroup) =>
    g.key === viewerId ? 0 : g.key === "none" ? 2 : 1;

  return Array.from(groups.values()).sort((a, b) => rank(a) - rank(b));
}

/** Due text for an action in the conversation brief. */
export function actionDueText(
  dueDate: number | null,
  now = new Date(),
): { text: string; overdue: boolean } {
  if (dueDate == null) {
    return { text: "No due date", overdue: false };
  }
  const overdue = fromUnixTime(dueDate) < now;
  return {
    text: `${overdue ? "Was due" : "Due"} ${formatDate(dueDate, now)}`,
    overdue,
  };
}
